import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { toast } from "sonner";

export const Reveal = ({ children, delay = 0, className = "", y = 32 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const Eyebrow = ({ children, light = false }) => (
  <div
    className={`flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.28em] ${
      light ? "text-[#C5A059]" : "text-[#B85D43]"
    }`}
  >
    <span className={`h-px w-8 ${light ? "bg-[#C5A059]" : "bg-[#C5A059]"}`} />
    {children}
  </div>
);

export const GoldDivider = ({ className = "" }) => (
  <div className={`gold-hairline w-40 mx-auto ${className}`} />
);

export const LeafMotif = ({ className = "w-10 h-10 text-[#C5A059]" }) => (
  <svg viewBox="0 0 48 48" fill="none" className={className} aria-hidden="true">
    <path
      d="M24 44C24 44 6 34 6 18C6 9 13 4 24 4C35 4 42 9 42 18C42 34 24 44 24 44Z"
      stroke="currentColor"
      strokeWidth="1.4"
    />
    <path d="M24 44V10" stroke="currentColor" strokeWidth="1.1" />
    <path d="M24 18L14 12M24 26L12 19M24 34L13 27M24 18L34 12M24 26L36 19M24 34L35 27" stroke="currentColor" strokeWidth="1" />
  </svg>
);

export const VideoPlaceholder = ({ label, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={() => toast.info("This video will be embedded here soon.")}
    className="group relative block w-full aspect-video rounded-3xl overflow-hidden bg-[#132C20] border border-[#C5A059]/30 hover:border-[#C5A059]/60 transition-colors duration-500 text-left"
  >
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(197,160,89,0.14),transparent_60%)]" />
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
      <span className="w-16 h-16 rounded-full bg-[#C5A059]/95 flex items-center justify-center animate-pulse-ring group-hover:scale-110 transition-transform duration-500">
        <Play className="w-6 h-6 text-[#1B3B2B] fill-[#1B3B2B] ml-0.5" />
      </span>
      <span className="text-[10px] tracking-[0.3em] uppercase text-[#FAF7F2]/45">Video Coming Soon</span>
    </div>
    <span className="absolute bottom-4 left-5 text-[10px] sm:text-[11px] tracking-[0.22em] uppercase text-[#C5A059]/90 font-semibold">
      {label}
    </span>
  </button>
);

export const YouTubeEmbed = ({ embedId, title, testid }) => (
  <div
    data-testid={testid}
    className="relative aspect-video rounded-3xl overflow-hidden bg-[#132C20] border border-[#C5A059]/30 shadow-[0_24px_60px_rgb(27,59,43,0.16)]"
  >
    <iframe
      src={`https://www.youtube.com/embed/${embedId}?rel=0`}
      title={title}
      loading="lazy"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      className="absolute inset-0 h-full w-full"
    />
  </div>
);

export const DoctorImage = ({ className = "", rounded = "rounded-t-[999px] rounded-b-3xl" }) => (
  <div className={`relative overflow-hidden bg-[#EAF0EC] ${rounded} ${className}`}>
    <img
      src="/doctor.jpg"
      alt="Dr. Santhisree Bheesetti, B.A.M.S — Founder & Chief Physician, Sree Prathama Ayurvedam"
      className="h-full w-full object-cover"
      style={{ objectPosition: "center 22%" }}
      data-testid="doctor-portrait-image"
      onError={(e) => {
        e.currentTarget.style.display = "none";
        e.currentTarget.nextSibling.style.display = "flex";
      }}
    />
    <div
      className="absolute inset-0 hidden flex-col items-center justify-center bg-gradient-to-b from-[#EAF0EC] to-[#dce7de] text-center px-6"
      style={{ display: "none" }}
    >
      <div className="w-28 h-28 rounded-full border border-[#C5A059] flex items-center justify-center bg-[#FAF7F2] shadow-[0_8px_30px_rgb(27,59,43,0.08)]">
        <span className="font-serif text-4xl text-[#1B3B2B]">SB</span>
      </div>
      <p className="mt-5 font-serif text-xl text-[#1B3B2B]">Dr. Santhisree Bheesetti</p>
      <p className="text-xs tracking-[0.2em] uppercase text-[#5C6E62] mt-1">B.A.M.S · Founder & Chief Physician</p>
      <p className="mt-4 text-[11px] text-[#8a968e]">Official portrait — add doctor.jpg to /public</p>
    </div>
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#1B3B2B]/10 via-transparent to-transparent" />
  </div>
);
